'use client'
import { Button } from '@/components/ui/deprecated-button'
import { useCustomPush } from '@/lib/client/navigation'
import { PaginatedMeta, SalaryType } from '@/lib/generated/graphql-types'
import { FlagSalaryButton } from '@/modules/salary/common/flag-salary'
import { LikeSalaryButton } from '@/modules/salary/common/like-salary'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { JSX, useCallback, useEffect } from 'react'
import { SalaryFilterParam } from '../constants'
import {
  PaginationDirection,
  useSalaryTableStore,
} from '@/modules/salary/salary-table-context'
import { useShareTracking } from '@/modules/salary/analytics/use-share-tracking'
import { ShareSalaryV2 } from '../share/share-salary-v2'

interface SalaryDetailsFooterProps {
  salaries: SalaryType[]
  salary: SalaryType
  meta?: PaginatedMeta
  isAuthenticated: boolean
  onTrackEvent: (action: 'share' | 'report' | 'like') => void
  countPublicInteraction: () => void
}

export function SalaryDetailsFooter({
  salaries,
  salary,
  meta,
  isAuthenticated,
  onTrackEvent,
  countPublicInteraction,
}: SalaryDetailsFooterProps): JSX.Element {
  const push = useCustomPush()
  const searchParams = useSearchParams()
  const paginationDirection = useSalaryTableStore(
    (state) => state.paginationDirection,
  )
  const setPaginationDirection = useSalaryTableStore(
    (state) => state.setPaginationDirection,
  )
  const page = parseInt(searchParams.get(SalaryFilterParam.page) || '1', 10)
  const { trackShare } = useShareTracking()
  const currentIndex = salaries.findIndex(
    (s) => s.salaryId === salary?.salaryId,
  )

  const updateSalaryId = useCallback(
    (offset: number) => {
      countPublicInteraction()
      const newIndex = currentIndex + offset
      // when salary still in page results just update the salaryId
      if (newIndex >= 0 && newIndex < salaries.length) {
        push({ salaryId: salaries[newIndex].salaryId })
      } else {
        // when needs to change page, update the page and set the direction
        // so can load first/last salary of the new page
        const newPage = (Math.max(page, 1) + (offset > 0 ? 1 : -1)).toString()
        setPaginationDirection(
          offset > 0
            ? PaginationDirection.Forward
            : PaginationDirection.Backwards,
        )
        push({ [SalaryFilterParam.page]: newPage })
      }
    },
    [
      currentIndex,
      salaries,
      push,
      page,
      setPaginationDirection,
      countPublicInteraction,
    ],
  )

  const isPreviousDisabled = currentIndex === 0 && (!page || page === 1)
  const isNextDisabled =
    (page === meta?.pagesCount &&
      meta?.totalCount % meta?.pageSize === currentIndex + 1) ||
    // for unAuth users we only allow them to see first page
    (!isAuthenticated && currentIndex === salaries.length - 1)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isPreviousDisabled && event.key === 'ArrowLeft') {
        updateSalaryId(-1)
      } else if (!isNextDisabled && event.key === 'ArrowRight') {
        updateSalaryId(1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [updateSalaryId, isPreviousDisabled, isNextDisabled])

  return (
    <>
      <Button
        variant="outline"
        customType="icon"
        onClick={() => updateSalaryId(-1)}
        isDisabled={isPreviousDisabled || Boolean(paginationDirection)}
        className="shrink-0"
      >
        <ChevronLeft className="stroke-copy-accent" size={18} />
      </Button>
      {isAuthenticated && salary ? (
        <div className="flex-1 flex justify-center gap-1">
          <ShareSalaryV2
            iconClassName={'w-5 h-5 stroke-topNav-unselected'}
            textClassName={'hidden xl:block'}
            isAuthenticated={isAuthenticated}
            isInPane
            onMouseDown={() => trackShare('pane', salary.salaryId, salary)}
          />
          <LikeSalaryButton
            salaryId={salary.salaryId}
            likes={salary.userLikes}
            className="w-5 h-5 stroke-topNav-unselected"
            onClick={() => onTrackEvent('like')}
          />
          <FlagSalaryButton
            salaryId={salary.salaryId}
            className="w-5 h-5 stroke-topNav-unselected"
            onClick={() => onTrackEvent('report')}
          />
        </div>
      ) : null}
      <Button
        variant="outline"
        customType="icon"
        onClick={() => updateSalaryId(1)}
        isDisabled={isNextDisabled || Boolean(paginationDirection)}
        className="shrink-0"
      >
        <ChevronRight className="stroke-copy-accent" size={18} />
      </Button>
    </>
  )
}
